import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Stack,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";

/**
 * Modal de confirmación para eliminar un rango de precios.
 *
 * No gestiona toasts ni cierra por su cuenta: quien lo monta decide qué hacer
 * tras onConfirm (éxito o error) y permanece abierto/operativo si el borrado
 * falla, para permitir reintentar.
 */
export default function ModalEliminarRango({ show, onHide, onConfirm, rango, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fff5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <WarningAmberIcon sx={{ color: "#dc3545", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" }
                        }}
                    >
                        {isMobile ? "Eliminar" : "Confirmar eliminación"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: "#6c757d",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)"
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial"
                }}
            >
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    {/* Mensaje de confirmación */}
                    <Typography
                        sx={{
                            fontSize: { xs: 14, sm: 15 },
                            color: "#495057",
                            lineHeight: 1.6
                        }}
                    >
                        ¿Estás seguro de que deseas <strong style={{ color: "#dc3545" }}>eliminar</strong> este
                        rango de precios?
                    </Typography>

                    {/* Información del rango a eliminar */}
                    {rango && (
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                backgroundColor: "#f8f9fa",
                                borderRadius: 2,
                                border: "1px solid #e9ecef"
                            }}
                        >
                            <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                        <LocationOnIcon sx={{ fontSize: 14, color: "#6c757d" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, sm: 12 },
                                                fontWeight: 600,
                                                color: "#6c757d"
                                            }}
                                        >
                                            Isla:
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            fontWeight: 700,
                                            color: "#212529"
                                        }}
                                    >
                                        {rango.isla || "-"}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                        <LocalPostOfficeIcon sx={{ fontSize: 14, color: "#6c757d" }} />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, sm: 12 },
                                                fontWeight: 600,
                                                color: "#6c757d"
                                            }}
                                        >
                                            Rango de códigos postales:
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            color: "#495057",
                                            // Los rangos llegan como "38001,38002,38003": sin espacios son un
                                            // unico token que no puede partirse, ensanchaba el Dialog y sacaba
                                            // scroll horizontal. Se muestran separados y se permite partir.
                                            overflowWrap: "anywhere"
                                        }}
                                    >
                                        {rango.rango ? rango.rango.split(",").map((cp) => cp.trim()).filter(Boolean).join(", ") : "-"}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    {/* Advertencia */}
                    <Alert
                        severity="error"
                        sx={{
                            backgroundColor: "#fff5f5",
                            color: "#842029",
                            border: "1px solid #f8d7da",
                            "& .MuiAlert-icon": {
                                color: "#dc3545"
                            }
                        }}
                    >
                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600 }}>
                            Esta acción no se puede deshacer
                        </Typography>
                    </Alert>
                </Stack>
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": {
                            borderColor: "#495057",
                            backgroundColor: "rgba(0,0,0,0.04)"
                        }
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                            <DeleteForeverIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#dc3545",
                        "&:hover": {
                            backgroundColor: "#bb2d3b"
                        },
                        "&:disabled": {
                            backgroundColor: "#6c757d",
                            color: "#fff"
                        }
                    }}
                >
                    {loading ? "Eliminando..." : isMobile ? "Eliminar" : "Eliminar rango"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
